import React, { useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UploadSimple, FileArrowDown, CheckCircle, Warning, Queue } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ImportPage() {
  const { t } = useLang();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const preview = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/entries/import?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const runAsync = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/jobs/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Import queued — check Jobs to follow progress");
      nav("/app/jobs");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("cl_token") || "";
    const orgId = localStorage.getItem("cl_active_org") || "";
    fetch(`${API_BASE}/entries/template.csv`, {
      credentials: "include",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(orgId ? { "X-Org-Id": orgId } : {}),
      },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "carbon_import_template.csv";
        link.click();
      });
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Data ingest</div>
        <h1 className="font-display text-3xl font-semibold mt-1">{t("importTitle")}</h1>
        <p className="text-muted-foreground text-sm mt-2">
          Upload a CSV with columns: <code className="font-mono-data text-xs bg-secondary px-1 py-0.5 rounded">date, scope, factor_id, activity_data, facility, data_quality, notes</code>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">1. {t("chooseFile")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border file:border-border file:bg-secondary file:text-foreground file:cursor-pointer hover:file:bg-secondary/80"
              data-testid="import-file"
            />
            <Button variant="outline" onClick={downloadTemplate} data-testid="import-template">
              <FileArrowDown size={16} className="mr-2" /> {t("downloadTemplate")}
            </Button>
          </div>
          {file && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={preview} disabled={busy} data-testid="import-dryrun">
                {t("dryRun")} ({t("preview")})
              </Button>
              <Button onClick={runAsync} disabled={busy} data-testid="import-submit">
                <Queue size={16} className="mr-2" /> {t("queueImport")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg">2. Result</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1 border border-primary/30 rounded-md p-4 bg-primary/5">
                <div className="flex items-center gap-2 text-primary">
                  <CheckCircle weight="fill" /> <span className="text-sm font-medium font-mono-data uppercase">{result.dry_run ? t("preview") : "Imported"}</span>
                </div>
                <div className="font-display text-3xl mt-2">{result.would_insert}</div>
                <div className="text-xs text-muted-foreground">{t("rowsInserted")}</div>
              </div>
              <div className="flex-1 border border-destructive/30 rounded-md p-4 bg-destructive/5">
                <div className="flex items-center gap-2 text-destructive">
                  <Warning weight="fill" /> <span className="text-sm font-medium font-mono-data uppercase">Errors</span>
                </div>
                <div className="font-display text-3xl mt-2">{result.errors.length}</div>
                <div className="text-xs text-muted-foreground">{t("rowsFailed")}</div>
              </div>
            </div>

            {result.preview.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-2">{t("preview")}</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Scope</th>
                        <th className="text-left px-3 py-2">Factor</th>
                        <th className="text-right px-3 py-2">Activity</th>
                        <th className="text-right px-3 py-2">kgCO₂e</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.preview.map((p) => (
                        <tr key={p.row} className="border-b border-border/60">
                          <td className="px-3 py-1.5 font-mono-data">{p.row}</td>
                          <td className="px-3 py-1.5 font-mono-data">{p.date}</td>
                          <td className="px-3 py-1.5 font-mono-data">S{p.scope}</td>
                          <td className="px-3 py-1.5">{p.factor_name}</td>
                          <td className="px-3 py-1.5 text-right font-mono-data">{p.activity_data}</td>
                          <td className="px-3 py-1.5 text-right font-mono-data font-medium">{p.emissions_kg_co2e.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.errors.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-destructive font-mono-data mb-2">Errors</div>
                <ul className="text-sm text-destructive space-y-1">
                  {result.errors.map((er, i) => (
                    <li key={i} className="font-mono-data">Row {er.row}: {er.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
